import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import {
  ISuggestion,
  ISection,
} from '../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDTypography from '../../../../../../../../../../BaseComponents/Typography/EDTypography';
import { ISelectedAnalys } from '../../../../../EkonomiskAnalysPage';
import EDHighlightedText from '../../../../../../../../../../BaseComponents/Autocomplete/Components/EDHighlightedText';
import { getTextParts } from '../../../../../../../../../../BaseComponents/Autocomplete/Utils/AutocompleteUtils';
import { EDListItemButton } from '../../../../../../../../../../BaseComponents/List/Components/EDListItem';
import EDListItemText from '../../../../../../../../../../BaseComponents/List/Components/EDListItemText';
import { EKAnalysGroupingModeType } from '../FavoriteAnalysDialog/EkonomiskAnalysFavoriteAnalysDialog';
import EDTooltip from '../../../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import { isSameAnalys } from '../FavoriteAnalysDialog/Utils/EkonomiskAnalysFavoriteAnalysDialogUtils';

interface IProps {
  selectedAnalys?: ISelectedAnalys | null;
  searchValue?: string;
  section: ISection;
  analyser: ISuggestion[];
  onAnalysSelected: (
    analys: ISuggestion,
    codePrivilege: ISuggestion
  ) => (event: React.MouseEvent<any>) => void;
  renderListItem?: (
    analys: ISuggestion,
    kodBeh: ISection,
    middleComponent: React.ReactNode
  ) => React.ReactNode;
  groupingMode: EKAnalysGroupingModeType;
  classes?: any;
}

const styles = (theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(1),
  },
  itemContainer: {
    borderLeft: `2px solid #d7d7d7`,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    whiteSpace: 'normal',
  },
  selectedItem: {
    borderLeftColor: 'black',
  },
  fontSize: {
    fontSize: '0.875rem',
    fontWeight: 400,
  },
  secondaryText: {
    fontSize: '0.775rem',
  },
});

const EkonomiskAnalysSelectAnalysList: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = props;

  return (
    <div className={classes.container}>
      {props.analyser.map((suggestion, index) => {
        const isSelected =
          props.selectedAnalys &&
          isSameAnalys(
            {
              utdelningLopnr: props.selectedAnalys.analys.utdelningLopnr,
              idKod: undefined,
              rapportId: props.selectedAnalys.analys.id,
              kodbehorighet: props.selectedAnalys.codePrivilege.id,
            },
            {
              utdelningLopnr: suggestion.data.utdelatLopnr,
              idKod: undefined,
              rapportId: suggestion.value,
              kodbehorighet: suggestion.data.kodBehId,
            }
          );

        const infoComponent = (
          <EDListItemText
            disableTypography
            primary={
              <EDTypography
                className={classes.fontSize}
                color={isSelected ? undefined : 'textSecondary'}
                noWrap={false}
              >
                {!isSelected && (
                  <EDHighlightedText
                    parts={getTextParts(
                      suggestion.name,
                      props.searchValue || ''
                    )}
                  />
                )}
                {isSelected && suggestion.name}
                {suggestion.data.obligatorisk && (
                  <EDTooltip title="Obligatorisk">
                    <span>*</span>
                  </EDTooltip>
                )}
              </EDTypography>
            }
            secondary={
              <>
                <EDTooltip
                  title={
                    props.groupingMode === 'kodbeh'
                      ? 'Rapportkategori'
                      : 'Kodbehörighet'
                  }
                >
                  <EDTypography
                    color="textSecondary"
                    className={classes.secondaryText}
                  >
                    {props.groupingMode === 'kodbeh'
                      ? suggestion.data.kategori
                      : suggestion.data.kodBehName}
                  </EDTypography>
                </EDTooltip>
                {suggestion.data.baseSelection &&
                  suggestion.data.baseSelection.name && (
                    <EDTooltip title="Grundurval">
                      <EDTypography
                        color="textSecondary"
                        className={classes.secondaryText}
                      >
                        {suggestion.data.baseSelection.name}
                      </EDTypography>
                    </EDTooltip>
                  )}
              </>
            }
          />
        );

        return (
          <EDListItemButton
            disableTabbing
            key={`${index}:${suggestion.name}`}
            className={classNames(classes.itemContainer, 'arrowMoveTarget', {
              [classes.selectedItem]: isSelected,
            })}
            onClick={props.onAnalysSelected(
              suggestion,
              suggestion.data.kodBehId
            )}
            testId={`analys-${suggestion.name}`}
            dense
            disableGutters
          >
            {props.renderListItem &&
              props.renderListItem(suggestion, props.section, infoComponent)}
            {!props.renderListItem && infoComponent}
          </EDListItemButton>
        );
      })}
    </div>
  );
};

export default withStyles(EkonomiskAnalysSelectAnalysList, styles);
